import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ButtonLink from '@v2/shared/components/ButtonLink';
import ContactInfo, { Company } from '@v2/shared/components/ContactInfo';
import ContentCard from '@v2/shared/components/ContentCard';
import PageHeading from '@v2/shared/components/PageHeading';

/** MagSpot's customer service address, and the only way this page offers to reach them. */
const SUPPORT_EMAIL = 'hello@magspot.com';

export type Props = {
  /** This site's company record, for the address block. See `ContactInfo`. */
  company: Company;
  /** Which pieces of it this site publishes. See `ContactInfo`. */
  displayContactInfo: string[];
};

/**
 * /contact.
 *
 * No form, unlike TopMags' and DiscountMags' contact pages: MagSpot has never had one.
 * `public/contact.php` still renders this template and still carries the whole legacy
 * contact controller behind it, but the brand's own template has always ignored all of it
 * and offered a mailto instead, so this page is running copy, a link and the address
 * block.
 *
 * `prose` is right here, unlike on TopMags' contact page: everything in this card is
 * paragraphs and a list. The mailto button carries its own classes because prose would
 * otherwise underline it as a link.
 */
export default function Contact({ company, displayContactInfo }: Props) {
  return (
    <>
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Contact Us' }]} />

      <PageHeading eyebrow="Customer Information">Contact MagSpot.com</PageHeading>

      <div className="prose mt-6 flex flex-col gap-6">
        <ContentCard>
          <p>
            If you need any assistance with our site or your subscriptions, our friendly customer
            service team is available 24/7 to make things as easy as possible at{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>

          <p>Your satisfaction is our highest priority!</p>

          <p>Please include the following information so that we can better assist you, such as:</p>

          <ul>
            <li>Order number</li>
            <li>Your full name</li>
            <li>Your email address</li>
          </ul>

          {/* Left-aligned, and so is the block under it: the legacy pair were both
              right-aligned, which reads as an alignment of its own on a page whose
              every other card starts at the left margin. */}
          <p>
            <ButtonLink href={`mailto:${SUPPORT_EMAIL}`}>Contact Us Now</ButtonLink>
          </p>

          <ContactInfo company={company} displayContactInfo={displayContactInfo} />
        </ContentCard>
      </div>
    </>
  );
}

Contact.layoutName = 'Layouts.Institutional';
